import Plotly, { Layout } from "plotly.js/dist/plotly-myPlotly.js";
import React, { useEffect } from "react";
import { bodyFontFamily, chartFont } from "../../shared/global";
import { Merge } from "../../shared/TypeScriptHelpers";
import { ChartProps } from "./ChartHOC";
import { ChartDataType } from "./COREChart";
import { COREChartLegend, Legend } from "./COREChartLegend";
import { grey100, prepareChartColor } from "../Content/COREColour";
import {
  AggregatesChartProps,
  Trace,
} from "../../modules/chartBuilder/AggregatesChartV2";
import equal from "fast-deep-equal";
import { downloadCSVFile } from "./ChartUtils/csvUtils";
import { generateChartLegends } from "./ChartUtils/chartLegendHelpers";

export type DotPlotTrace = Pick<
  Trace,
  "name" | "line.color" | "line.dash" | "tagStyle"
> & {
  x: (number | null)[];
  y: string[];
  marker?: ChartDataType["marker"];
};

export type COREDotPlotChartProps = Merge<
  Pick<
    AggregatesChartProps,
    | "downloadPng"
    | "downloadCsv"
    | "testID"
    | "plotRef"
    | "chartPanel"
    | "chartTitle"
    | "chartLegendRef"
    | "empty"
    | "setProgressPercent"
    | "customTooltip"
    | "fitParent"
  >,
  {
    layout?: Partial<Layout>;
    traces: DotPlotTrace[];
    axisLabel: string;
  }
>;

const generateChartLayout = (
  layout: Partial<Plotly.Layout> | undefined,
  chartTitle: COREDotPlotChartProps["chartTitle"],
  axisLabel: COREDotPlotChartProps["axisLabel"]
): Partial<Layout> & {
  filename?: string;
} => {
  return {
    font: {
      family: chartFont,
    },
    filename: chartTitle,
    titlefont: {
      family: bodyFontFamily,
    },
    margin: {
      t: 30,
      r: 2,
    },
    hovermode: "closest",
    showlegend: false,
    xaxis: {
      showline: false,
      automargin: true,
      title: {
        text: axisLabel,
        standoff: 15,
        font: {
          family: chartFont,
        },
      },
      linecolor: grey100,
    },
    yaxis: {
      linecolor: "#F2F2F2",
      showline: false,
      showgrid: true,
    },
    ...layout,
  };
};

export const DotPlotChart: React.FC<COREDotPlotChartProps & ChartProps> = ({
  traces,
  testID,
  plotRef,
  chartTitle,
  axisLabel,
  chartPanel,
  downloadCsv,
  downloadPng,
  setOnDownloadCSV,
  setOnDownloadPNG,
  setOnResetChart,
  layout,
  chartLegendRef,
  empty,
  setProgressPercent,
  customTooltip,
  fitParent,
}) => {
  const tracesWithColor: DotPlotTrace[] = prepareChartColor(traces);
  const legends: Legend[] = generateChartLegends(tracesWithColor);
  const newTraces: Partial<ChartDataType>[] = tracesWithColor.map((item) => {
    return {
      ...item,
      type: "scatter",
      mode: "markers",
      hovertemplate: "%{y} %{x:,.3f} tCO₂e",
      marker: {
        ...item.marker,
        color: item.marker?.color ?? item["line.color"],
        size: item.marker?.size ?? 16,
        symbol: "circle",
      },
    };
  });
  setProgressPercent && setProgressPercent(1);
  const chartLayout = generateChartLayout(layout, chartTitle, axisLabel);

  useEffect(() => {
    const handleResetChart = () => {
      if (!plotRef?.current) {
        return;
      }
      Plotly.relayout(plotRef.current.el, {
        xaxis: {
          ...plotRef.current.props.layout.xaxis,
          autorange: true,
        },
        yaxis: {
          ...plotRef.current.props.layout.yaxis,
          autorange: true,
        },
      });
    };

    const handleDownloadPNG = () => {
      chartLegendRef?.current?.callDrawImage();
    };

    const handleDownloadCSV = () => {
      if (!traces || traces.length < 1) {
        return;
      }

      const labels = [...new Set(traces.flatMap((item) => item.y))];
      const values = traces.map((item) => {
        return item.x;
      });
      const csvData = labels.map((item, index) => {
        const row = [item];
        values.forEach((v) => {
          if (v && v[index] !== null) {
            row.push(String(v[index]));
          }
        });
        return row;
      });

      const csvDataWithHeader = [
        [axisLabel, ...traces.map((t) => t.name)],
        ...csvData,
      ];
      downloadCSVFile(csvDataWithHeader, true, chartTitle);
    };

    if (setOnResetChart) setOnResetChart(() => handleResetChart);
    if (setOnDownloadPNG) setOnDownloadPNG(() => handleDownloadPNG);
    if (setOnDownloadCSV) setOnDownloadCSV(() => handleDownloadCSV);
  }, [
    chartLegendRef,
    chartTitle,
    plotRef,
    traces,
    axisLabel,
    setOnDownloadCSV,
    setOnDownloadPNG,
    setOnResetChart,
  ]);

  return (
    <COREChartLegend
      chartId={testID}
      chartTitle={chartTitle}
      chartPanel={chartPanel}
      fitParent={fitParent}
      downloadCsv={downloadCsv ?? false}
      downloadPng={downloadPng}
      data={newTraces}
      layout={chartLayout}
      testID={testID}
      legends={legends}
      showlegend={true}
      legendFilter={true}
      plotRef={plotRef}
      ref={chartLegendRef}
      empty={empty}
      customTooltip={customTooltip}
    />
  );
};

export const COREDotPlotChart = React.memo<
  React.ComponentType<
    COREDotPlotChartProps & ChartProps & { ref?: React.ForwardedRef<any> }
  >
>(
  DotPlotChart,
  (prevProps, nextProps) =>
    prevProps &&
    equal(prevProps.layout, nextProps.layout) &&
    equal(prevProps.traces, nextProps.traces) &&
    // Without this the chart keeps the size it was drawn at when it is expanded
    // to full screen, since neither of the comparisons above changes.
    prevProps.fitParent === nextProps.fitParent
);
